<template>
  <div class="pricing-page">
    <div class="pr-wrap">
      <!-- ===== Header ===== -->
      <div class="pr-header reveal">
        <div class="pr-badge">{{ lg.pr_badge }}</div>
        <h1 class="pr-title">{{ lg.pr_title }}</h1>
        <p class="text-lg pr-subtitle">{{ lg.pr_subtitle }}</p>
      </div>

      <!-- ===== Plans ===== -->
      <div class="pr-plans">
        <div
          v-for="plan in plans"
          :key="plan.key"
          class="pr-plan reveal"
          :class="{ 'pr-plan--popular': plan.popular }"
        >
          <div v-if="plan.popular" class="pr-popular">{{ lg.pr_most_popular }}</div>

          <div class="mb-6">
            <h3 class="pr-plan-name">{{ lg['pr_plan_' + plan.key + '_name'] }}</h3>
            <div class="mb-2">
              <div v-if="plan.price === null" class="pr-price-custom">{{ lg.pr_custom }}</div>
              <div v-else class="pr-price-row">
                <span class="pr-price">฿{{ plan.price }}</span>
                <span class="pr-period">/{{ lg.pr_month }}</span>
              </div>
            </div>
            <p class="pr-plan-desc">{{ lg['pr_plan_' + plan.key + '_desc'] }}</p>
          </div>

          <button
            type="button"
            class="pr-plan-btn"
            :class="plan.popular ? 'pr-plan-btn--solid' : 'pr-plan-btn--outline'"
            @click="onClickPlan(plan.key)"
          >
            {{ lg['pr_plan_' + plan.key + '_cta'] }}
          </button>

          <div class="pr-feats">
            <div v-for="f in plan.features" :key="f.key" class="pr-feat">
              <v-icon
                size="20"
                :color="f.included ? '#215295' : '#d1d5db'"
                class="pr-feat-icon"
              >{{ f.included ? 'mdi-check' : 'mdi-close' }}</v-icon>
              <span :class="f.included ? 'pr-feat-on' : 'pr-feat-off'">
                {{ lg['pr_feat_' + f.key] }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== Fair usage ===== -->
      <section class="pr-fair reveal">
        <div class="pr-fair-box">
          <div class="pr-fair-icon">
            <v-icon size="20" color="#215295">mdi-information-outline</v-icon>
          </div>
          <div>
            <h3 class="pr-fair-title">{{ lg.pr_fair_title }}</h3>
            <p class="pr-fair-desc">{{ lg.pr_fair_desc }}</p>
          </div>
        </div>
      </section>

      <!-- ===== Recording storage ===== -->
      <section class="pr-storage reveal">
        <h2 class="pr-section-title">{{ lg.pr_storage_title }}</h2>
        <p class="pr-storage-desc">{{ lg.pr_storage_desc }}</p>
      </section>

      <!-- ===== FAQ ===== -->
      <section class="pr-faq">
        <h2 class="pr-section-title text-center mb-12 reveal">{{ lg.pr_faq_title }}</h2>
        <div class="pr-faq-list">
          <div v-for="n in 7" :key="n" class="pr-faq-item reveal">
            <h3 class="pr-faq-q">{{ lg['pr_faq_q' + n] }}</h3>
            <p class="pr-faq-a">{{ lg['pr_faq_a' + n] }}</p>
          </div>
        </div>
      </section>

      <!-- ===== CTA ===== -->
      <section class="pr-cta">
        <div class="reveal">
          <h2 class="pr-cta-title">{{ lg.pr_cta_title }}</h2>
          <p class="pr-cta-subtitle">{{ lg.pr_cta_subtitle }}</p>
          <div class="pr-cta-actions">
            <button type="button" class="cta-btn cta-btn--white" @click="onClickMenu('/register-account')">
              {{ lg.pr_cta_start }}
            </button>
            <button type="button" class="cta-btn cta-btn--outline" @click="onClickMenu('/contact')">
              {{ lg.pr_cta_contact }}
            </button>
          </div>
        </div>
      </section>
    </div>
    <LoadingDialog></LoadingDialog>
  </div>
</template>

<script>
import { server, feature } from "../services/constants.js";
import api from "@/services/api";
import { lg_header_pricing } from "./language/lg_header_pricing.js";
import LoadingDialog from "@/components/cards/LoadingDialog";

export default {
  name: "PricingPage",
  components: { LoadingDialog },
  data: () => ({
    lg: {},
    plans: [
      {
        key: "free",
        price: "0",
        popular: false,
        features: [
          { key: "10pax", included: true },
          { key: "40min", included: true },
          { key: "screen", included: true },
          { key: "chat", included: true },
          { key: "fair_usage", included: true },
          { key: "no_record", included: false },
          { key: "no_vote", included: false },
          { key: "no_minutes", included: false },
        ],
      },
      {
        key: "pro",
        price: "490",
        popular: false,
        features: [
          { key: "50pax", included: true },
          { key: "unlimited", included: true },
          { key: "screen", included: true },
          { key: "chat", included: true },
          { key: "fair_usage", included: true },
          { key: "60rec", included: true },
          { key: "no_minutes", included: false },
          { key: "no_vote", included: false },
        ],
      },
      {
        key: "business",
        price: "1,490",
        popular: true,
        features: [
          { key: "100pax", included: true },
          { key: "unlimited", included: true },
          { key: "screen", included: true },
          { key: "chat", included: true },
          { key: "voting", included: true },
          { key: "vote_reports", included: true },
          { key: "fair_usage", included: true },
          { key: "120rec", included: true },
          { key: "opt_legal_minutes", included: true },
        ],
      },
      {
        key: "enterprise",
        price: null,
        popular: false,
        features: [
          { key: "custom_pax", included: true },
          { key: "custom_usage", included: true },
          { key: "custom_rec", included: true },
          { key: "rtmp", included: true },
          { key: "adv_vote_reports", included: true },
          { key: "legal_minutes", included: true },
          { key: "priority", included: true },
        ],
      },
    ],
    scrollHandler: null,
    timers: [],
  }),
  created() {
    this.$store.state.page_reload = false;
    this.$store.state.loadFooter = true;
    this.lg =
      localStorage.getItem(feature.LANGUAGE) === "th"
        ? lg_header_pricing.lg_header_pricing_th
        : lg_header_pricing.lg_header_pricing_en;
  },
  mounted() {
    const userId = localStorage.getItem(server.USER_ID);
    if (userId && api.isLoggedIn()) {
      this.$router.push("/listroom");
      return;
    }

    // animation (เหมือน Mainpage / About)
    const targets = Array.from(this.$el.querySelectorAll(".reveal"));
    const inView = (el) => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const rect = el.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < vh - 100;
    };
    let order = 0;
    targets.forEach((el) => {
      if (inView(el)) {
        this.timers.push(
          setTimeout(() => el.classList.add("reveal--visible"), 200 + order++ * 150)
        );
      }
    });
    this.scrollHandler = () =>
      targets.forEach((el) => el.classList.toggle("reveal--visible", inView(el)));
    document.addEventListener("scroll", this.scrollHandler);
  },
  beforeDestroy() {
    this.cleanup();
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    cleanup() {
      if (this.scrollHandler) {
        document.removeEventListener("scroll", this.scrollHandler);
        this.scrollHandler = null;
      }
      this.timers.forEach(clearTimeout);
      this.timers = [];
    },
    // ปุ่มในการ์ดแพ็กเกจ (พฤติกรรมเดิม)
    onClickPlan(key) {
      if (key === "free") {
        this.onClickMenu("/register-account");
      } else if (key === "enterprise") {
        this.onClickMenu("/contact");
      }
      // pro / business: เดิมเป็น coming soon ยังไม่มี action
      // เปิดจ่ายเงินเมื่อพร้อม: this.PaycheckoutPro(key);
    },
    async PaycheckoutPro(plan_name) {
      localStorage.setItem(feature.GET_BUY_NOW, `${plan_name}`);
      this.$router.push(`/register-account`);
    },
    onClickMenu(link) {
      this.$router.push(link).catch(() => {});
    },
  },
};
</script>

<style scoped>
.pricing-page {
  min-height: 100vh;
  padding: 80px 0;
  background: #ffffff;
  color: #0a0a0a;
}
.pr-wrap {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
}
@media (min-width: 640px) {
  .pr-wrap { padding: 0 24px; }
}
@media (min-width: 1024px) {
  .pr-wrap { padding: 0 32px; }
}
.text-lg {
  font-size: 1.25rem;
  line-height: 1.75rem;
  color: #4b5563;
  margin: 0;
}
.mb-2 { margin-bottom: 8px; }
.mb-6 { margin-bottom: 24px; }
.mb-12 { margin-bottom: 48px !important; }

/* Header */
.pr-header {
  text-align: center;
  margin-bottom: 64px;
}
.pr-badge {
  display: inline-block;
  padding: 8px 16px;
  border-radius: 9999px;
  font-size: 0.875rem;
  margin-bottom: 24px;
  background-color: rgba(33, 82, 149, 0.1);
  color: #215295;
}
.pr-title {
  font-size: 3rem;
  line-height: 1.1;
  font-weight: 500;
  margin: 0 0 16px;
}
.pr-subtitle {
  max-width: 42rem;
  margin: 0 auto;
}

/* Plans */
.pr-plans {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-bottom: 80px;
  align-items: start;
}
@media (min-width: 768px) {
  .pr-plans { grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 1024px) {
  .pr-plans { grid-template-columns: repeat(4, 1fr); }
}
.pr-plan {
  position: relative;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  padding: 24px;
  transition: box-shadow 0.2s ease, opacity 0.8s ease, transform 0.8s ease;
}
.pr-plan:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
}
.pr-plan--popular {
  border-color: #215295;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
}
.pr-plan--popular.reveal--visible {
  transform: scale(1.05);
}
.pr-popular {
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 16px;
  border-radius: 9999px;
  background: #215295;
  color: #ffffff;
  font-size: 0.875rem;
  white-space: nowrap;
}
.pr-plan-name {
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0 0 8px;
}
.pr-price-row {
  display: flex;
  align-items: baseline;
}
.pr-price {
  font-size: 2.25rem;
  line-height: 2.5rem;
}
.pr-period {
  color: #4b5563;
  margin-left: 8px;
}
.pr-price-custom {
  font-size: 1.875rem;
  line-height: 2.25rem;
}
.pr-plan-desc {
  color: #4b5563;
  font-size: 0.875rem;
  margin: 0;
}
.pr-plan-btn {
  display: block;
  width: 100%;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  margin-bottom: 24px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.pr-plan-btn--solid {
  background: #215295;
  color: #ffffff;
  border: 2px solid #215295;
}
.pr-plan-btn--outline {
  background: #ffffff;
  color: #215295;
  border: 2px solid #215295;
}
.pr-plan-btn--outline:hover {
  background: #f9fafb;
}
.pr-feats {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.pr-feat {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.875rem;
}
.pr-feat-icon {
  flex-shrink: 0;
  margin-top: 1px;
}
.pr-feat-on { color: #111827; }
.pr-feat-off { color: #9ca3af; }

/* Fair usage */
.pr-fair {
  max-width: 56rem;
  margin: 0 auto 80px;
}
.pr-fair-box {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  padding: 24px;
}
.pr-fair-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(33, 82, 149, 0.1);
}
.pr-fair-title {
  font-size: 1rem;
  font-weight: 500;
  color: #215295;
  margin: 0 0 8px;
}
.pr-fair-desc {
  color: #374151;
  margin: 0;
}

/* Storage */
.pr-storage {
  text-align: center;
  padding: 64px 32px;
  border-radius: 16px;
  margin-bottom: 80px;
  background: rgba(33, 82, 149, 0.05);
}
.pr-section-title {
  font-size: 1.875rem;
  line-height: 2.25rem;
  font-weight: 500;
  margin: 0 0 12px;
}
.pr-storage-desc {
  color: #4b5563;
  max-width: 42rem;
  margin: 0 auto 48px;
}

/* FAQ */
.pr-faq {
  margin-bottom: 80px;
}
.pr-faq-list {
  max-width: 48rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.pr-faq-item {
  background: #ffffff;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #f3f4f6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.pr-faq-q {
  font-size: 1rem;
  font-weight: 500;
  color: #215295;
  margin: 0 0 8px;
}
.pr-faq-a {
  color: #4b5563;
  margin: 0;
}

/* CTA */
.pr-cta {
  text-align: center;
  padding: 64px 32px;
  border-radius: 16px;
  background-color: #215295;
}
.pr-cta-title {
  font-size: 1.875rem;
  line-height: 2.25rem;
  font-weight: 500;
  color: #ffffff;
  margin: 0 0 16px;
}
.pr-cta-subtitle {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 32px;
}
.pr-cta-actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
}
@media (min-width: 640px) {
  .pr-cta-actions { flex-direction: row; }
}
.cta-btn {
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.cta-btn--white {
  background: #ffffff;
  color: #215295;
  border: 2px solid #ffffff;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}
.cta-btn--white:hover {
  background: #f3f4f6;
  border-color: #f3f4f6;
}
.cta-btn--outline {
  background: transparent;
  color: #ffffff;
  border: 2px solid #ffffff;
}
.cta-btn--outline:hover {
  background: rgba(255, 255, 255, 0.1);
}

@media (max-width: 599px) {
  .pr-title { font-size: 2.25rem; }
}

/* Animation */
.reveal {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}
.reveal--visible {
  opacity: 1;
  transform: translateY(0);
}
@media (prefers-reduced-motion: reduce) {
  .reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>